'use client'

import Link from 'next/link'
import s from './FooterComponent.module.scss'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { PortableText } from '@portabletext/react'
import { portableBlockComponents } from '@/utils/portableText'
import { usePathname } from 'next/navigation'

type Props = { data: any }

export default function FooterComponent({ data }: Props) {
  const footerRef = useRef<HTMLElement | null>(null)
  const pathname = usePathname()

  const nameRef = useRef<HTMLInputElement>(null);
  const companyRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const [result, setResult] = useState<Response | null>(null);

  const subscribeUser = async (e: any) => {
      e.preventDefault();

      const res = await fetch('/api/subscribeUser', {
          body: JSON.stringify({
              email: emailRef.current ? emailRef.current.value : '',
              name: nameRef.current ? nameRef.current.value : '',
              company: companyRef.current ? companyRef.current.value : ''
          }),
          headers: {
              'Content-Type': 'application/json'
          },
          method: 'POST'
      });

      setResult(res);
      return res;
  };


  useLayoutEffect(() => {
    const el = footerRef.current
    if (!el) return

    const setVar = (h: number) => {
      document.documentElement.style.setProperty('--footer-h', `${Math.ceil(h)}px`)
    }

    // 1) Medición inicial (tras el layout)
    const raf = requestAnimationFrame(() => setVar(el.offsetHeight))

    // 2) Reaccionar a cambios de tamaño del footer (contenido dinámico, fuentes, etc.)
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) setVar(entry.contentRect.height)
    })
    ro.observe(el)

    // 3) Asegurar tras carga completa (imágenes, fuentes)
    const onLoad = () => setVar(el.offsetHeight)
    window.addEventListener('load', onLoad)

    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      window.removeEventListener('load', onLoad)
    }
  // Si cambias de ruta en Next, vuelve a medir
  }, [pathname])



  return (
    <footer ref={footerRef} className={s.footer}>
      <section className={s.newsletter} aria-labelledby="footer-newsletter-heading">
        <div className={s.newsletterContent}><h2 id="footer-newsletter-heading">Newsletter</h2></div>
        <div className={s.newsletterText}><p>{data?.newsletterText}</p></div>
        <div className={s.newsletterForm}>
          <form onSubmit={subscribeUser}>
            <input type="text" name="name" ref={nameRef} placeholder="Name and Surname" required />
            <input type="text" name="company" ref={companyRef} placeholder="Company" />
            <input type="email" name="email" ref={emailRef} placeholder="Email address" required />
            <div className={s.terms}>
                <label className={s.customCheckbox}>
                    <input type="checkbox" name="terms" id="terms" required />
                    <span></span>
                    <small>I have read and accept the <Link href="/legal/privacy-policy">terms and conditions</Link></small>
                </label>
            </div>
            <button type="submit">↗ Subscribe</button>
            <div className={s.answer}>
                {result && result.status === 200 ? <p>{"Thanks for subscribing"}</p> : result && result.status === 400 ? <p>{"You're already subscribed"}</p> : undefined}
            </div>
          </form>
        </div>
      </section>

      <section className={s.bottom} aria-labelledby="footer-contact-heading">
        <div className={s.contact}><h2 id="footer-contact-heading">Contact</h2></div>
        <address className={s.contactInfo}>
          {data?.address?.map((textRow: any) => (
            <PortableText
              key={textRow._key}
              value={[textRow]}
              components={portableBlockComponents()}
            />
          ))}
        </address>
        <nav className={s.socials} aria-label="Social media">
          {data?.footerLinks?.map((link: any, i: number) => (
            <div key={i} className={s.socialLink}>
              <span className={s.socialType} translate="no">{link.socialType}</span>
              <Link className={s.link} href={link.url} target="_blank" rel="noopener noreferrer" translate="no">{link.title}</Link>
            </div>
          ))}
        </nav>
        <nav className={s.policies} aria-label="Legal">
          <ul>
            {data?.policyLinks?.map((policy: any, i: number) => (
              <li key={i} className={s.policyLink}>
                <Link href={policy.url}>{policy.title}</Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className={s.copyright}>
          <p translate='no'>{data?.copyright}</p>
        </div>
      </section>
    </footer>
  )
}